import { observer } from 'mobx-react-lite';
import {LinearProgress, TextField, Typography, Select, MenuItem, MenuProps, FormControl, RadioGroup, FormControlLabel, Radio, makeStyles, Tooltip } from '@material-ui/core';
import { Checkbox, Stack } from '@mui/material';
import { SIAutocomplete, SIAutocompleteMultiSelect, SelectionItem } from '../../controls/Autocomplete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export type InputItem = {
    type: 'select' | 'input' | 'multiline' | 'checkbox' | 'selection' | 'multiselect' | 'radiogroup',
    label: string,
    onChange: (item?: any) => void,
    items?: SelectionItem[],
    value?: string | boolean | number,
    isReady: () => boolean,
    showLoading: () => boolean,
    disabled?: boolean,
    selectedItem?: any, // selected item value (id) only!
    onAddSelectionItem?: (item: SelectionItem) => void,
    verifyAdd?: (item: SelectionItem) => boolean,
    canAdd?: () => boolean,
    infoIconText?: string,
    placeholder?: string,
    maxLength?: number,
    htmlType?: string,
    inputProps?: { min?: number, max?: number, step?: number }
}

type CopilotInputItemsProps = {
    inputItems: InputItem[]
}

const menuProps: Partial<MenuProps> = {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
        vertical: 'top',
        horizontal: 'left',
    },
    getContentAnchorEl: null, // This prevents the menu from selecting the first item as its anchor
    PaperProps: {
        style: {
          transform: 'translate(-5px, 4px)'
        }
    }
  }


const useStyles = makeStyles({
      radioLabel: {
        '& .MuiFormControlLabel-label': {
          fontWeight: 500
        }
      }    
});
  


export const CopilotInputItems = observer(({ inputItems }: CopilotInputItemsProps) => {
    const classes = useStyles();

    return (<Stack spacing={2} width='100%'>
                {inputItems.map(ii =>
                    <Stack spacing={0.5} key={ii.label}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography style={{ color: ii.disabled ? '#0000004d' : 'black', fontWeight: ii.type == 'radiogroup' ? 500 : 300  }}>
                                {ii.label}
                            </Typography>
                        {
                            ii.infoIconText && 
                            <Tooltip title={ii.infoIconText}>
                                <InfoOutlinedIcon fontSize="small" style={{ color: ii.disabled ? '#0000004d' : 'gray' }} />
                            </Tooltip>
                        }
                    </Stack>
                        {ii.type === 'select' ?
                            ii.isReady() ?
                                <SIAutocomplete 
                                    items={ii.items!}
                                    initial_selection={typeof ii.selectedItem === 'object' && ii.selectedItem !== null ? ii.selectedItem as SelectionItem : ii.items!.find(it => it.value === ii.selectedItem) ?? undefined}
                                    onSelect={ii.onChange}
                                    onAddSelectionItem={ii.onAddSelectionItem}
                                    verifyAdd={ii.verifyAdd}
                                    style={{ width: '100%' }}
                                    canAdd={ii.canAdd}
                                    loadProgress={()=> ii.showLoading() as boolean}
                                    disabled={ii.disabled}
                                /> :
                                <div key={ii.label}>
                                    <TextField label="Loading" variant="outlined" style={{ width: '100%' }} disabled={true} />
                                    {ii.showLoading() && <LinearProgress />}
                                </div>
                            : ii.type === 'input' ? <TextField variant="outlined" type={ii.htmlType} inputProps={ii.inputProps} placeholder={`Enter a ${ii.label.toLowerCase()}`} value={ii.value} onChange={(e) => ii.onChange(e as any)} disabled={ii.disabled} />
                            : ii.type === 'multiline' ? <TextField variant="outlined" multiline inputProps={{ maxLength: ii.maxLength || 1000 }} rows={5}  placeholder={ii.placeholder} value={ii.value} onChange={(e) => ii.onChange(e as any)} disabled={ii.disabled}/>
                            : ii.type === 'checkbox' ? <Checkbox checked={ii.value as boolean} onChange={(e) => ii.onChange(e as any)} style={{ width: '20px' }} disabled={!ii.isReady()|| ii.disabled}></Checkbox>
                            : ii.type === 'selection' ?
                                (ii.items!.length > 100 ? (
                                    <SIAutocomplete
                                        items={ii.items!}
                                        initial_selection={ii.items!.find(it => it.value === ii.selectedItem) ?? undefined}
                                        onSelect={(item) => ii.onChange(item?.value)}
                                        style={{ width: '100%' }}
                                        loadProgress={() => false}
                                        disabled={!ii.isReady() || ii.disabled}
                                    />
                                ) : (
                                <Select
                                  value={ii.selectedItem}
                                  onChange={event => ii.onChange(event.target.value as any)}
                                  variant='outlined'
                                  disabled={!ii.isReady()|| ii.disabled}
                                  MenuProps={menuProps}
                                >
                                    {ii.items!.map(item => <MenuItem key={item.value} value={item.value} disabled={item.disabled}>{item.label}</MenuItem>)}
                                </Select>
                                ))
                            :  ii.type === 'multiselect' ?
                                <SIAutocompleteMultiSelect
                                    items={ii.items!}
                                    initial_selection={ii.selectedItem}
                                    onSelect={ii.onChange} 
                                    style={{ width: '100%' }}
                                    loadProgress={()=> ii.showLoading() as boolean}
                                    disabled={ii.disabled}/>
                            : ii.type === 'radiogroup' ? 
                                <FormControl component="fieldset">
                                    <RadioGroup aria-label={ii.label} name={ii.label} value={ii.selectedItem} onChange={ii.onChange}>
                                        {ii.items!.map(item=><FormControlLabel
                                                              key={item.value}
                                                              disabled={ii.disabled}
                                                              value={item.value}
                                                              control={<Radio />}
                                                              label={item.label}
                                                              className={classes.radioLabel}
                                                              />)}
                                    </RadioGroup>
                                </FormControl>
                            :<></>}
                    </Stack>)}
            </Stack>)
})
